/**
 * PUSH NOTIFICATIONS HELPER
 * 
 * Handles:
 * - Requesting notification permissions
 * - Getting and storing FCM tokens
 * - Managing notification preferences
 * 
 * Location: src/firebase/notifications.ts
 */

import { getMessaging, getToken, onMessage, type MessagePayload } from 'firebase/messaging';
import { doc, setDoc, deleteDoc, collection, getDocs } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { db } from './config';

// Your VAPID key from Firebase Console
const VAPID_KEY = 'BJSDBBTtwN65IIHRcYeVWt3bzudl99NPUWcomt5IQjXJVwVtNFk8hobZVc4_n9vDDSJYEQHBxW_vFFpsXUgSlRw';

// Get messaging instance
let messaging: ReturnType<typeof getMessaging> | null = null;

/**
 * Initialize Firebase Messaging
 * Must be called after Firebase app is initialized
 */
const getMessagingInstance = () => {
  if (!messaging) {
    try {
      const app = getApp();
      messaging = getMessaging(app);
    } catch (error) {
      console.error('Error initializing messaging:', error);
      return null;
    }
  }
  return messaging;
};

/**
 * Check if notifications are supported in this browser
 */
export const isNotificationsSupported = (): boolean => {
  return (
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window
  );
};

/**
 * Get current notification permission status
 * @returns 'granted' | 'denied' | 'default' | 'unsupported'
 */
export const getNotificationPermission = (): NotificationPermission | 'unsupported' => {
  if (!isNotificationsSupported()) {
    return 'unsupported';
  }
  return Notification.permission;
};

/**
 * Request notification permission from user
 * @returns Permission status after request
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission | 'unsupported'> => {
  if (!isNotificationsSupported()) {
    return 'unsupported';
  }

  try {
    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    throw error;
  }
};

/**
 * Get FCM token for this browser/device
 * Requires notification permission to be granted first
 */
export const getFCMToken = async (): Promise<string | null> => {
  const messagingInstance = getMessagingInstance();
  if (!messagingInstance) {
    console.error('Messaging not initialized');
    return null;
  }

  // Check permission first
  if (Notification.permission !== 'granted') {
    return null;
  }

  try {
    // Register service worker if not already registered
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

    // Get the token
    const token = await getToken(messagingInstance, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    if (token) {
      return token;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting FCM token:', error);
    throw error;
  }
};

/**
 * Save FCM token to Firestore for a user
 * Stores in: users/{uid}/fcmTokens/{tokenId}
 */
export const saveFCMToken = async (userId: string, token: string): Promise<void> => {
  try {
    // Create a unique ID for this token based on browser/device
    const tokenId = btoa(token.slice(0, 50)).replace(/[^a-zA-Z0-9]/g, '');
    
    const tokenRef = doc(db, 'users', userId, 'fcmTokens', tokenId);
    await setDoc(tokenRef, {
      token,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      userAgent: navigator.userAgent,
      // Helps identify device type
      platform: navigator.platform || 'unknown'
    });
  } catch (error) {
    console.error('Error saving FCM token:', error);
    throw error;
  }
};

/**
 * Remove FCM token from Firestore (when user disables notifications)
 */
export const removeFCMToken = async (userId: string, token: string): Promise<void> => {
  try {
    const tokenId = btoa(token.slice(0, 50)).replace(/[^a-zA-Z0-9]/g, '');
    const tokenRef = doc(db, 'users', userId, 'fcmTokens', tokenId);
    await deleteDoc(tokenRef);
  } catch (error) {
    console.error('Error removing FCM token:', error);
    throw error;
  }
};

/**
 * Enable push notifications for a user
 * Complete flow: request permission → get token → save to Firestore
 */
export const enablePushNotifications = async (userId: string): Promise<{
  success: boolean;
  permission: NotificationPermission | 'unsupported';
  token?: string;
  error?: string;
}> => {
  // Check support
  if (!isNotificationsSupported()) {
    return {
      success: false,
      permission: 'unsupported',
      error: 'Push notifications are not supported in this browser'
    };
  }

  // Request permission
  const permission = await requestNotificationPermission();
  
  if (permission !== 'granted') {
    return {
      success: false,
      permission,
      error: permission === 'denied' 
        ? 'Notifications were blocked. Please enable them in your browser settings.'
        : 'Notification permission was not granted'
    };
  }

  // Get token
  try {
    const token = await getFCMToken();
    
    if (!token) {
      return {
        success: false,
        permission,
        error: 'Failed to get notification token'
      };
    }

    // Save token to Firestore
    await saveFCMToken(userId, token);

    return {
      success: true,
      permission,
      token
    };
  } catch (error: any) {
    return {
      success: false,
      permission,
      error: error.message || 'Failed to enable notifications'
    };
  }
};

/**
 * Disable push notifications for a user
 */
export const disablePushNotifications = async (userId: string): Promise<void> => {
  try {
    const token = await getFCMToken();
    if (token) {
      await removeFCMToken(userId, token);
    }
  } catch (error) {
    console.error('Error disabling notifications:', error);
  }
};

/**
 * Listen for foreground messages (when app is open and in focus)
 * @param callback Function to call when a message is received
 * @returns Unsubscribe function
 */
export const onForegroundMessage = (
  callback: (payload: MessagePayload) => void
): (() => void) => {
  const messagingInstance = getMessagingInstance();
  if (!messagingInstance) {
    console.error('Messaging not initialized');
    return () => {};
  }

  return onMessage(messagingInstance, (payload) => {
    callback(payload);
  });
};

/**
 * Show a local notification (for foreground messages)
 * Since the service worker only handles background messages,
 * we need to manually show notifications when the app is in focus
 */
export const showLocalNotification = (
  title: string,
  options?: NotificationOptions
): void => {
  if (Notification.permission === 'granted') {
    new Notification(title, {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      ...options
    });
  }
};

/**
 * Get all FCM tokens for a user (useful for debugging)
 */
export const getUserFCMTokens = async (userId: string): Promise<string[]> => {
  try {
    const tokensRef = collection(db, 'users', userId, 'fcmTokens');
    const snapshot = await getDocs(tokensRef);
    return snapshot.docs.map(doc => doc.data().token);
  } catch (error) {
    console.error('Error getting user tokens:', error);
    return [];
  }
};

/**
 * Refresh FCM token and update Firestore
 * Should be called on app load for users who have notifications enabled
 * This ensures tokens stay fresh and valid
 */
export const refreshFCMToken = async (userId: string): Promise<string | null> => {
  // Check if notifications are supported and permission is granted
  if (!isNotificationsSupported() || Notification.permission !== 'granted') {
    return null;
  }

  try {
    const token = await getFCMToken();

    if (token) {
      // Save/update the token in Firestore
      await saveFCMToken(userId, token);
      return token;
    }

    return null;
  } catch (error) {
    console.error('Error refreshing FCM token:', error);
    return null;
  }
};

/**
 * Initialize push notifications on app load
 * Call this when a user logs in or the app starts with an authenticated user
 * Automatically refreshes tokens if notifications are enabled
 */
export const initializePushNotifications = async (userId: string): Promise<void> => {
  // Only proceed if notifications are supported and already granted
  if (!isNotificationsSupported() || Notification.permission !== 'granted') {
    return;
  }

  try {
    // Refresh the token silently
    await refreshFCMToken(userId);
  } catch (error) {
    // Silently fail - don't interrupt app loading
    console.error('Error initializing push notifications:', error);
  }
};

/**
 * Diagnostic function to troubleshoot push notification issues
 * Call from browser console: diagnosticPushNotifications()
 */
export const diagnosePushNotifications = async (userId?: string): Promise<{
  supported: boolean;
  permission: NotificationPermission | 'unsupported';
  serviceWorker: 'registered' | 'not-registered' | 'error';
  serviceWorkerDetails?: string;
  token: string | null;
  tokenError?: string;
  firestoreTokens: string[];
  tokenMatch: boolean;
}> => {
  const result: any = {
    supported: isNotificationsSupported(),
    permission: isNotificationsSupported() ? Notification.permission : 'unsupported',
    serviceWorker: 'not-registered',
    token: null,
    firestoreTokens: [],
    tokenMatch: false
  };

  // Check service worker
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const fcmWorker = registrations.find(r => r.active?.scriptURL.includes('firebase-messaging-sw.js'));
    if (fcmWorker) {
      result.serviceWorker = 'registered';
      result.serviceWorkerDetails = `Active: ${fcmWorker.active?.state}, Scope: ${fcmWorker.scope}`;
    } else if (registrations.length > 0) {
      result.serviceWorker = 'not-registered';
      result.serviceWorkerDetails = `Found ${registrations.length} workers but none for FCM: ${registrations.map(r => r.active?.scriptURL).join(', ')}`;
    }
  } catch (err: any) {
    result.serviceWorker = 'error';
    result.serviceWorkerDetails = err.message;
  }

  // Try to get FCM token
  if (result.supported && result.permission === 'granted') {
    try {
      result.token = await getFCMToken();
    } catch (err: any) {
      result.tokenError = err.message;
    }
  }

  // Check Firestore tokens
  if (userId) {
    result.firestoreTokens = await getUserFCMTokens(userId);
    result.tokenMatch = result.token ? result.firestoreTokens.includes(result.token) : false;
  }

  // Log results
  console.log('=== Push Notification Diagnostics ===');
  console.log('Supported:', result.supported);
  console.log('Permission:', result.permission);
  console.log('Service Worker:', result.serviceWorker, result.serviceWorkerDetails || '');
  console.log('Current Token:', result.token ? `${result.token.substring(0, 20)}...` : 'None');
  if (result.tokenError) console.log('Token Error:', result.tokenError);
  console.log('Firestore Tokens:', result.firestoreTokens.length);
  console.log('Token in Firestore:', result.tokenMatch);
  console.log('=====================================');

  return result;
};

// Make diagnostic function available globally for console access
if (typeof window !== 'undefined') {
  (window as any).diagnosePushNotifications = diagnosePushNotifications;
}
